import React, { useImperativeHandle, forwardRef } from 'react';
import { metaStyle as meta } from '../utils/shopHealth';
import { needsAttention } from '../utils/shopPresentation';
import { useShopOffer, outcomeRowFor } from '../hooks/useShopOffer';
import ShopTable from './ShopTable';

// THE FEEDS HUB: the shops that supply this publisher's images, one scannable
// line each, trouble first, and a link per line to the room that holds the rest.
//
// WHAT IT USED TO BE. Until 2026-09-06 this component rendered the whole screen:
// four prose blocks and up to five buttons per shop, 43 shops deep, followed by
// the 11 shops the publisher could add. Driven at 390px at the live account's
// size the page was 19.1 screens, and every other thing on `/feeds` sat below
// all of it, receding 0.40 of a phone screen for every shop the publisher added.
//
// It now holds ONE variable-length collection and nothing else — the shops you
// could add moved to `/feeds/add`, the forms to their own routes, and every
// action to `/feeds/:ref`. That is what makes the other entries' depth
// independent of how many shops there are.
//
// Three rules that survived the split unchanged:
//
// 1. THE ORDER IS THE SERVER'S — but the GROUPING is this screen's, and those
//    are different things. Rows render as received INSIDE each group. The server
//    ranks by the merchant prior, which knows nothing about our own sync state,
//    so the one stopped feed on the live account sat at position 18 of 32.
//    `shop-list.ts` already records grouping as the portal's job ("the portal
//    groups by supply, so that term is now the grouping"); this is a second
//    partition on the same served field, not a re-sort.
//
// 2. ONE STATE, AND IT IS THE SERVER'S. `supply` is not re-derived from
//    `syncEnabled` and `lastSyncError`: those two columns cannot tell a feed the
//    scheduler struck out from one the publisher paused.
//
// 3. A FLAGGED SHOP IS SHOWN, NOT HIDDEN. Grouping it first is the opposite of
//    hiding it.

export interface ShopListRef {
  refresh: () => void;
}

interface Props {
  /** Carried across a navigation from a room whose shop no longer exists. */
  notice?: string | null;
}

const ShopList = forwardRef<ShopListRef, Props>(({ notice }, ref) => {
  const { shops, failed, outcome, staleFeedIds, reload } = useShopOffer();
  useImperativeHandle(ref, () => ({ refresh: () => { void reload(); } }), [reload]);

  if (failed) {
    return (
      <section className="stats-section" data-testid="shop-list-error">
        <h2>Your shops</h2>
        <p role="alert" style={{ color: 'var(--danger)' }}>
          We could not load your shops just now. Reload the page to try again — nothing has changed.
        </p>
      </section>
    );
  }

  if (!shops) {
    return (
      <section className="stats-section">
        <h2>Your shops</h2>
        <p style={meta}>Loading…</p>
      </section>
    );
  }

  const supplied = shops.filter(s => s.supplied);
  const attention = supplied.filter(s => needsAttention(s, staleFeedIds));
  const healthy = supplied.filter(s => !needsAttention(s, staleFeedIds));
  const outcomeFor = (shop: typeof supplied[number]) => outcomeRowFor(outcome, shop);

  return (
    <section className="stats-section" data-testid="shop-list">
      {notice && <p role="status" data-testid="shop-list-notice">{notice}</p>}

      <h2>Your shops{supplied.length > 0 && ` (${supplied.length})`}</h2>
      {supplied.length === 0 ? (
        <p style={meta} data-testid="no-shops">
          Nothing supplies your images yet. Use one of the links at the top of this page to add a
          shop from your network, upload a feed file, or connect one by URL.
        </p>
      ) : (
        <>
          {attention.length > 0 && (
            <div data-testid="shops-attention" style={{ marginTop: '0.5rem' }}>
              <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>Needs you ({attention.length})</h3>
              <ShopTable shops={attention} outcomeFor={outcomeFor} testId="shop-table-attention" />
            </div>
          )}
          <div style={{ marginTop: attention.length > 0 ? '1.25rem' : '0.5rem' }}>
            {attention.length > 0 && (
              <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>Running ({healthy.length})</h3>
            )}
            <ShopTable
              shops={healthy}
              outcomeFor={outcomeFor}
              testId="shop-table-running"
              caption={
                <p style={meta}>
                  The shops your images can sell from, best first — open one for its figures and
                  everything you can do with it.
                </p>
              }
            />
          </div>
        </>
      )}

      {/* THE EXCEPTION IS THE POINT. This said "these figures" without one, and
          was true when it was written — every number on a row came from the
          network. It stopped being true on 2026-09-05, when "shown" and
          "clicked" arrived: the one figure that IS about this publisher's
          readers sat directly above a sentence denying that any such figure
          existed. Adding a figure to a line means revisiting this sentence. */}
      <p style={{ ...meta, marginTop: '0.75rem' }}>
        Apart from “shown” and “clicked”, which are yours, these figures describe how each
        shop performs for affiliates across the whole network — not for your readers
        specifically.
      </p>
    </section>
  );
});

ShopList.displayName = 'ShopList';

export default ShopList;
